import { useCallback, useMemo } from 'react';
import { isReferralStartParam } from '@app/shared';
import { isApiFailure } from './api/http';
import { ApiProvider, useAppApi } from './app/ApiProvider';
import { SessionProvider, useSession } from './app/SessionProvider';
import { I18nProvider, useI18n, type I18nValue } from './i18n/I18nProvider';
import { useNavigation } from './nav/useNavigation';
import { roleOf, showsTabBar, type Route } from './nav/routes';
import { tabsFor } from './nav/tabs';
import { BookingScreen } from './screens/client/BookingScreen';
import { BusinessScreen } from './screens/client/BusinessScreen';
import { CatalogScreen } from './screens/client/CatalogScreen';
import { MyAppointmentsScreen } from './screens/client/MyAppointmentsScreen';
import { FeedbackScreen } from './screens/client/FeedbackScreen';
import { PolicyScreen } from './screens/client/PolicyScreen';
import { ProfileScreen } from './screens/client/ProfileScreen';
import { StartScreen } from './screens/client/StartScreen';
import { AdminScreen } from './screens/admin/AdminScreen';
import { StatsScreen } from './screens/business/StatsScreen';
import { CalendarScreen } from './screens/business/CalendarScreen';
import { HomeScreen } from './screens/business/HomeScreen';
import { OnboardingScreen } from './screens/business/OnboardingScreen';
import { PlanScreen } from './screens/business/PlanScreen';
import { BlacklistScreen } from './screens/business/BlacklistScreen';
import { RulesScreen } from './screens/business/RulesScreen';
import { ScheduleScreen } from './screens/business/ScheduleScreen';
import { ServicesScreen } from './screens/business/ServicesScreen';
import { SettingsScreen } from './screens/business/SettingsScreen';
import { StaffScreen } from './screens/business/StaffScreen';
import { TimeOffScreen } from './screens/business/TimeOffScreen';
import type { TelegramContext } from './telegram/telegram';
import { TabBar } from './ui/TabBar';
import { Alert } from './ui/feedback';
import { HomeContext } from './ui/Screen';

/**
 * Корень приложения.
 *
 * Порядок провайдеров значим: язык нужен ошибкам API, поэтому `I18nProvider` снаружи.
 * `ApiProvider` берёт `initData` функцией — значение читается в момент запроса, а не
 * в момент сборки дерева. `SessionProvider` идёт последним, потому что вход — это тоже
 * запрос: он берёт клиента из `ApiProvider` и переводит свой отказ словарём `I18nProvider`.
 *
 * Провайдер сессии обёрнут вокруг ВСЕГО дерева, но ничего собой не загораживает: вход
 * запускается один раз на старте и идёт фоном (ТЗ §5). Экраны витрины не ждут его вовсе —
 * иначе клиент, открывший приложение по QR-наклейке, смотрел бы на спиннер вместо
 * заведения, к которому он пришёл записаться.
 */
export function App(props: { telegram: TelegramContext }): JSX.Element {
  const { telegram } = props;
  const getInitData = useCallback(() => telegram.rawInitData, [telegram.rawInitData]);

  return (
    <I18nProvider telegramLanguageCode={telegram.languageCode}>
      <ApiProvider getInitData={getInitData}>
        <SessionProvider>
          <Shell telegram={telegram} />
        </SessionProvider>
      </ApiProvider>
    </I18nProvider>
  );
}

function Shell(props: { telegram: TelegramContext }): JSX.Element {
  const { telegram } = props;
  const { t, describeError } = useI18n();
  const session = useSession();

  /**
   * Отказ входа, о котором стоит говорить.
   *
   * Показывается ТОЛЬКО статус `failed` — подпись была, а аккаунт не завёлся. Состояние
   * `unsigned` (приложение открыто в браузере, подписывать нечем) плашки не получает
   * намеренно: вне Telegram подписи нет по определению, и красный баннер на каждом
   * открытии ссылки в браузере — крик на ровном месте. Причина при этом не теряется:
   * защищённое действие всё равно откажет ключом `err.noInitData` с внятным текстом
   * «откройте из чата с ботом» (см. `withSession` в `api/client.ts`).
   */
  const sessionError = session.state.status === 'failed' ? session.state.error : null;

  /**
   * Стартовый экран.
   *
   * `start_param` из deep-link несёт slug организации (ТЗ §5): по QR-наклейке клиент обязан
   * попасть сразу на её страницу, а не в общий каталог, где ему пришлось бы искать
   * заведение руками.
   *
   * Без deep-link открывается стартовый экран с выбором «Записаться» / «Принимать записи»
   * (решение владельца 21 сентября 2026, по макету). Заставка 16 сентября убиралась как
   * лишний шаг перед каталогом; новая — не заставка, а развилка клиент/бизнес.
   */
  const initialRoute = useMemo<Route>(
    () =>
      // `ref_…` — реферальная метка, а не slug заведения (slug символа `_` не содержит):
      // её обработал сервер при входе, открывать «заведение ref_…» незачем.
      telegram.startParam && !isReferralStartParam(telegram.startParam)
        ? { name: 'business', slug: telegram.startParam }
        : { name: 'start' },
    [telegram.startParam],
  );

  const nav = useNavigation(initialRoute);
  // Набор вкладок зависит от маршрута, а не только от роли: у кабинета они требуют
  // открытой организации (см. `tabsFor`).
  const tabs = tabsFor(nav.route);
  // Кнопка «на главную» в шапке каждого экрана, кроме самой главной. `tab`, а не `push`:
  // стек сбрасывается, и «назад» с главной не уводит обратно вглубь.
  const goHome = useCallback(() => nav.tab({ name: 'start' }), [nav]);

  return (
    <HomeContext.Provider value={nav.route.name === 'start' ? null : goHome}>
    <div className="relative h-full">
      {/* Общая полоса верхних плашек. Один контейнер на обе, а не два: с двумя отступ
          сверху сложился бы вдвое, когда SDK не поднялся И вход не удался. */}
      {telegram.setupError || sessionError !== null ? (
        <div className="px-4 pt-[calc(env(safe-area-inset-top,0px)+14px)]">
          {/* Провал инициализации SDK внутри Telegram виден на экране: без сообщения
              «приложение не грузится» осталось бы загадкой и для клиента, и для поддержки. */}
          {telegram.setupError ? (
            <Alert tone="danger" title={t('errTitle')}>
              {telegram.setupError}
            </Alert>
          ) : null}

          {/* Вход не удался при живой подписи. Плашка объясняет и причину, и последствие —
              «каталог работает, запись нет», — и даёт повтор: сеть на старте могла моргнуть,
              и второй попытки достаточно. Молча оставить приложение без аккаунта нельзя
              (AGENTS.md, правило 7): защищённые действия после этого не работают. */}
          {sessionError !== null ? (
            <Alert
              tone="danger"
              title={t('sessionFailedT')}
              action={{ label: t('retry'), onClick: session.retry }}
            >
              <div>{failureText(sessionError, describeError)}</div>
              <div className="mt-1">{t('sessionFailedB')}</div>
            </Alert>
          ) : null}
        </div>
      ) : null}

      <Screens nav={nav} />

      {showsTabBar(nav.route) ? (
        <TabBar
          tabs={tabs}
          active={nav.route.name}
          onSelect={(tab) => nav.tab(tab.route)}
        />
      ) : null}
    </div>
    </HomeContext.Provider>
  );
}

/**
 * Текст отказа для плашки.
 *
 * Повторяет правило `ErrorState`: ключ API переводится словарём, всё остальное показывается
 * своим сообщением. Ни одной ветки «что-то пошло не так»: по ключу разбирают инцидент,
 * и подменять его общей фразой значит прятать поломку (AGENTS.md, правило 7).
 */
function failureText(error: unknown, describe: I18nValue['describeError']): string {
  if (isApiFailure(error)) return describe({ key: error.key, details: error.details }).text;
  return error instanceof Error ? error.message : String(error);
}

function Screens(props: { nav: ReturnType<typeof useNavigation> }): JSX.Element {
  const { nav } = props;
  const { route } = nav;
  const api = useAppApi();

  /**
   * «Назад» на корневом экране. Вкладка обнуляет стек (`nav.tab`), и `back()` там снимать
   * нечего — стрелка в шапке была нарисована, но не работала. Поэтому при пустой истории
   * «назад» ведёт на уровень выше: вкладки кабинета → список организаций → стартовый экран.
   */
  const up: Route =
    roleOf(route) === 'business' && route.name !== 'bizHome' ? { name: 'bizHome' } : { name: 'start' };
  const back = nav.canGoBack ? nav.back : () => nav.tab(up);

  switch (route.name) {
    case 'start':
      return (
        <StartScreen
          onBook={() => nav.tab({ name: 'catalog' })}
          onBusiness={() => nav.push({ name: 'bizHome' })}
          onAdmin={() => nav.push({ name: 'admin' })}
        />
      );

    case 'policy':
      return <PolicyScreen onBack={back} />;

    case 'feedback':
      return <FeedbackScreen onBack={back} />;

    case 'catalog':
      return (
        <CatalogScreen onOpenBusiness={(slug) => nav.push({ name: 'business', slug })} />
      );

    case 'business':
      return (
        <BusinessScreen
          slug={route.slug}
          onBack={back}
          onBook={(serviceId) =>
            nav.push(
              serviceId
                ? { name: 'booking', slug: route.slug, serviceId }
                : { name: 'booking', slug: route.slug },
            )
          }
        />
      );

    case 'booking':
      return (
        <BookingScreen
          slug={route.slug}
          initialServiceId={route.serviceId}
          onBack={back}
          onBooked={() => nav.tab({ name: 'my' })}
        />
      );

    case 'my':
      return (
        <MyAppointmentsScreen
          onOpenBusiness={(slug) => nav.push({ name: 'business', slug })}
        />
      );

    case 'profile':
      return (
        <ProfileScreen onFeedback={() => nav.push({ name: 'feedback' })} />
      );

    case 'admin':
      return (
        <AdminScreen
          onBack={back}
          onOpenBusiness={(businessId) => nav.push({ name: 'adminStats', businessId })}
        />
      );

    case 'adminStats':
      // Тот же экран статистики, что у владельца, но источник — админский (любой бизнес).
      return (
        <StatsScreen
          businessId={route.businessId}
          onBack={back}
          load={(period) => api.admin.businessStats(route.businessId, period)}
        />
      );

    // ── Кабинет бизнеса ──

    case 'bizHome':
      return (
        <HomeScreen
          onOpenBusiness={(businessId) => nav.tab({ name: 'calendar', businessId })}
          onCreate={() => nav.push({ name: 'bizOnboarding' })}
          onBack={back}
        />
      );

    case 'bizOnboarding':
      return (
        <OnboardingScreen
          // После создания — сразу в календарь новой организации, а не назад в список:
          // владелец пришёл сюда работать, а не любоваться строкой в перечне.
          onCreated={(businessId) => nav.tab({ name: 'calendar', businessId })}
          onBack={back}
        />
      );

    case 'calendar':
      return <CalendarScreen businessId={route.businessId} onBack={back} />;

    case 'bizSettings':
      return (
        <SettingsScreen
          businessId={route.businessId}
          onBack={back}
          onOpen={(name) => nav.push({ name, businessId: route.businessId })}
          onFeedback={() => nav.push({ name: 'feedback' })}
          // Ушла в историю — на «Мой бизнес», а не назад: открытый календарь и настройки
          // относились к организации, которой на главном экране уже нет.
          onArchived={() => nav.tab({ name: 'bizHome' })}
        />
      );

    case 'bizServices':
      return <ServicesScreen businessId={route.businessId} onBack={back} />;

    case 'bizStaff':
      return <StaffScreen businessId={route.businessId} onBack={back} />;

    case 'bizSchedule':
      return <ScheduleScreen businessId={route.businessId} onBack={back} />;

    case 'bizTimeOff':
      return <TimeOffScreen businessId={route.businessId} onBack={back} />;

    case 'bizRules':
      return <RulesScreen businessId={route.businessId} onBack={back} />;

    case 'bizBlacklist':
      return <BlacklistScreen businessId={route.businessId} onBack={back} />;

    case 'bizPlan':
      return <PlanScreen businessId={route.businessId} onBack={back} />;

    case 'bizStats':
      return <StatsScreen businessId={route.businessId} onBack={back} />;
  }
}
